import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { first } from 'rxjs/operators';
import { TagJoinService } from '../tag-join.service';
import { AlertService } from '@app/_services';

export interface TagSelectModalData
{
	availableTags: any[];
	selectedTags: any[];
	tagSource: number;
	tagSourceId: number;
	tagType: number;
}

interface TagGroup
{
	label: string;
	tags: any[];
}

@Component({
	selector: 'app-tag-select-modal',
	templateUrl: './tag-select-modal.component.html',
	styleUrls: ['./tag-select-modal.component.css'],
	standalone: false
})
export class TagSelectModalComponent implements OnInit
{
	tagGroups: TagGroup[] = [];
	selectedTagIds: Set<number> = new Set();
	searchQuery: string = '';

	constructor(
		private dialogRef: MatDialogRef<TagSelectModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: TagSelectModalData,
		private tagJoinService: TagJoinService,
		private alertService: AlertService,
	) { }

	ngOnInit()
	{
		// Build selected tag IDs set
		if (this.data.selectedTags)
		{
			this.data.selectedTags.forEach(tag => this.selectedTagIds.add(tag.tagId));
		}

		// Group available tags by tagGroupCategory
		this.buildTagGroups();
	}

	buildTagGroups()
	{
		const groupMap: { [key: string]: any[] } = {};
		const uncategorized: any[] = [];
		const query = (this.searchQuery || '').trim().toLowerCase();

		if (this.data.availableTags)
		{
			this.data.availableTags.forEach(tag =>
			{
				if (query && !(tag.name || '').toLowerCase().includes(query)) return;
				const cat = tag.tagGroupCategory;
				if (cat)
				{
					if (!groupMap[cat]) groupMap[cat] = [];
					groupMap[cat].push(tag);
				}
				else
				{
					uncategorized.push(tag);
				}
			});
		}

		this.tagGroups = [];
		// Build groups in the order categories are encountered
		Object.keys(groupMap).sort().forEach(cat =>
		{
			this.tagGroups.push({ label: cat, tags: groupMap[cat] });
		});
		// Add uncategorized at end if any
		if (uncategorized.length > 0)
		{
			this.tagGroups.push({ label: 'Other', tags: uncategorized });
		}
	}

	isSelected(tag: any): boolean
	{
		return this.selectedTagIds.has(tag.tagId);
	}

	/**
	 * R1 (Approach A) — Tags wired to an order product (orderProductId set on
	 * the tag) are read-only here. The only legitimate way to add or remove
	 * them is via order-product mutations on the invoice; manually unchecking
	 * one would silently delete the OP from the reservation, which is
	 * surprising. Backend also rejects manual unassign as a safety net.
	 */
	isOpWired(tag: any): boolean
	{
		return tag != null && tag.orderProductId != null;
	}

	/** Tooltip text shown on the lock icon next to OP-wired tags. */
	opWiredTooltip(tag: any): string
	{
		return 'Auto-applied from order product ' + tag.orderProductId
			+ '. Add or remove this tag by editing the order product on the invoice.';
	}

	onToggle(tag: any, event: any)
	{
		// Read-only enforcement (FE side). The backend also rejects so a stale
		// browser state can't bypass this.
		if (this.isOpWired(tag)) {
			// Force the checkbox back to its server-truth state (it'll be the
			// "currently selected" status from selectedTagIds).
			event.source.checked = this.selectedTagIds.has(tag.tagId);
			this.alertService.error({ error: { message: this.opWiredTooltip(tag) } });
			return;
		}

		if (event.checked)
		{
			this.selectedTagIds.add(tag.tagId);
			let payload: any = {};
			payload.tagId = tag.tagId;
			payload.tagSource = this.data.tagSource;
			payload.tagSourceId = this.data.tagSourceId;
			this.tagJoinService.addTagJoins(payload).pipe(first()).subscribe(
				data => { this.alertService.success(data, true); },
				error => { this.alertService.error(error); }
			);
		}
		else
		{
			this.selectedTagIds.delete(tag.tagId);
			let payload: any = {};
			payload.tagSource = this.data.tagSource;
			payload.tagSourceId = this.data.tagSourceId;
			payload.tagId = tag.tagId;
			this.tagJoinService.deleteTagJoin(payload).pipe(first()).subscribe(
				data => { this.alertService.success(data, true); },
				error => { this.alertService.error(error); }
			);
		}
		// Reassign Set so Angular detects the change and re-evaluates [checked] bindings
		this.selectedTagIds = new Set(this.selectedTagIds);
	}

	getSelectedTags(): any[]
	{
		const allTags = this.data.availableTags || [];
		return allTags.filter(t => this.selectedTagIds.has(t.tagId));
	}

	close()
	{
		this.dialogRef.close(this.getSelectedTags());
	}
}
